import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Linkedin, Twitter } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import PageBanner from '../components/Banner';

const AVATAR_COLORS = [
  ['#E8611A', '#fff7ed'],
  ['#3b82f6', '#eff6ff'],
  ['#16a34a', '#f0fdf4'],
  ['#7c3aed', '#f5f3ff'],
  ['#0891b2', '#ecfeff'],
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

const MemberCard = ({ member, colorIndex }) => {
  const [hovered, setHovered] = useState(false);
  const [accent, bg] = AVATAR_COLORS[colorIndex % AVATAR_COLORS.length];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => member.profile_link && window.open(member.profile_link, '_blank')}
      style={{
        background: hovered ? '#fff' : 'rgba(255,255,255,0.88)',
        borderRadius: 20, overflow: 'hidden',
        boxShadow: hovered ? '0 20px 48px rgba(0,0,0,0.12)' : '0 4px 16px rgba(0,0,0,0.07)',
        border: hovered ? `1px solid ${accent}35` : '1px solid rgba(0,0,0,0.06)',
        transition: 'transform .25s, box-shadow .25s, border-color .25s',
        transform: hovered ? 'translateY(-6px)' : 'none',
        cursor: member.profile_link ? 'pointer' : 'default',
      }}
    >
      {/* Zone image / avatar */}
      <div style={{
        height: 240, position: 'relative', overflow: 'hidden',
        background: member.image
          ? `url(${member.image}) center/cover no-repeat`
          : `linear-gradient(135deg,${bg} 0%,${accent}20 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!member.image && (
          <div style={{
            width: 96, height: 96, borderRadius: '50%',
            background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 6px 24px ${accent}55`,
            fontSize: '2rem', fontWeight: 800, color: '#fff', letterSpacing: '-1px',
            fontFamily: "'DM Sans',sans-serif",
          }}>
            {getInitials(member.nom_prenom)}
          </div>
        )}
        {/* Overlay dégradé hover */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(to top, ${accent}cc 0%, transparent 55%)`,
          opacity: hovered ? 1 : 0, transition: 'opacity .3s',
        }} />
        {/* Icônes sociales */}
        <div style={{
          position: 'absolute', bottom: 16, left: 0, width: '100%',
          display: 'flex', justifyContent: 'center', gap: 12,
          opacity: hovered ? 1 : 0, transition: 'opacity .3s',
        }}>
          {member.linkedin && (
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'transform .2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <Linkedin size={18} color="#0A66C2" />
            </a>
          )}
          {member.twitter && (
            <a href={member.twitter} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'transform .2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <Twitter size={18} color="#1DA1F2" />
            </a>
          )}
        </div>
      </div>

      {/* Infos */}
      <div style={{ padding: '18px 22px 22px', fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            {member.nom_prenom}
          </h3>
        </div>
        <p style={{ fontSize: '0.82rem', color: accent, fontWeight: 600, margin: '0 0 10px 16px', letterSpacing: '0.03em' }}>
          {member.role}
        </p>
        <div style={{ width: 32, height: 3, borderRadius: 99, background: `linear-gradient(to right,${accent},${accent}55)`, marginLeft: 16 }} />
      </div>
    </div>
  );
};

const Equipe = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/aydees/liste-equipe/')
      .then(res => setMembers(res.data))
      .catch(err => console.error('Erreur:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Notre Équipe - Aydees | Experts IT et Digital au Niger</title>
        <meta name="description" content="Découvrez l'équipe d'experts Aydees : développeurs, consultants, designers et spécialistes IT passionnés qui donnent vie à vos projets au Niger." />
        <meta name="keywords" content="équipe Aydees, experts IT, développeurs, consultants, Niger, Niamey" />
      </Helmet>

      <PageBanner
        title="Notre Équipe"
        subtitle="Des experts passionnés qui donnent vie à vos projets"
      />

      <section style={{
        background: 'linear-gradient(135deg,#fff7ed 0%,#ffffff 50%,#f8fafc 100%)',
        padding: '56px 24px 64px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.4, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle,#e2e8f0 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        <div style={{ position: 'absolute', top: '-8%', right: '8%', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle,rgba(232,97,26,0.12) 0%,transparent 70%)', filter: 'blur(55px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-8%', left: '5%', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle,rgba(59,130,246,0.08) 0%,transparent 70%)', filter: 'blur(55px)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {members.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '1rem' }}>Aucun membre pour le moment.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 24 }}>
              {members.map((member, index) => (
                <MemberCard key={member.id || index} member={member} colorIndex={index} />
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Equipe;